/**
 * Pre-save validation for lesson packs. Pure function over `slides[]` —
 * surfaces structural problems the editor wants the teacher to fix before
 * publishing.
 *
 * **Non-blocking** — returns a list of issues for the UI to render. Saves
 * aren't gated on this; teachers can keep editing and resolve as they go.
 *
 * Issues covered (see backend SCHEMA_USAGE.md §"Editor structural-change
 * rules"):
 *   - Orphan slide: no inbound pointer + not the entry beat.
 *   - Dangling pointer: `next.*` / `targetBeatId` names a beat id that no
 *     longer exists.
 *   - Ghost quiz: mcq element sits on a slide whose `beatMeta.kind !== "quiz"`.
 *   - Half-ghost quiz: more than one mcq on a `kind:"quiz"` slide.
 *   - Quiz consistency:
 *       a) element `correctOptionId` ≠ beatMeta `correctOptionId`
 *       b) beatMeta.branches keys ≠ element optionIds minus correctOptionId
 *       c) correctOptionId not in element.optionIds
 */

import { beatIdOf, findOrphanSlides, findPointersTo } from './routing';
import type { McqContent, ScriptElement, Slide } from './schema.types';

export type IssueKind =
  | 'orphan-slide'
  | 'dangling-pointer'
  | 'ghost-quiz'
  | 'half-ghost-quiz'
  | 'correct-option-mismatch'
  | 'missing-branch'
  | 'extra-branch'
  | 'correct-option-unknown';

export interface PackIssue {
  kind: IssueKind;
  /** Which slide's the offender — used for the "go to slide" jump. */
  slideId: string;
  /** Short headline for the banner row. */
  title: string;
  /** One sentence on what's wrong + how to fix. */
  detail: string;
  /** `'warning'` = pack still works but is fragile; `'error'` = classroom
   *  playback will visibly fail (orphan, dangling pointer, ghost quiz). */
  severity: 'warning' | 'error';
}

export interface ValidationOptions {
  /** The pack's top-level entry beat id. Excluded from the orphan check
   *  (it's expected to have no inbound pointer). */
  entryBeatId?: string | null;
}

export function validatePack(
  slides: Slide[],
  opts: ValidationOptions = {},
): PackIssue[] {
  const issues: PackIssue[] = [];
  const entryBeatId = opts.entryBeatId ?? null;

  // Build a set of every existing beat id for the dangling-pointer scan.
  const knownBeats = new Set<string>();
  for (const s of slides) {
    const b = beatIdOf(s);
    if (b) knownBeats.add(b);
  }

  // ---- orphan slides ----
  for (const s of findOrphanSlides(slides, entryBeatId)) {
    issues.push({
      kind: 'orphan-slide',
      slideId: s.id,
      severity: 'error',
      title: `Orphan slide: ${s.label || s.id}`,
      detail:
        'No other slide routes to this one, and it isn’t the starting slide — so the lesson can never reach it. Use the routing dropdowns to point an earlier slide at it.',
    });
  }

  // ---- dangling pointers + per-slide quiz checks ----
  for (const s of slides) {
    const bm = s.beatMeta;
    const beatId = beatIdOf(s);

    // Dangling pointers — collected by scanning every other slide for
    // references to a nonexistent beat. Easier here: walk this slide's
    // own pointers and check each target against `knownBeats`.
    if (bm) {
      const nextKeys = ['default', 'ifExpanded', 'ifReteach', 'ifSkipped'] as const;
      for (const k of nextKeys) {
        const v = bm.next?.[k];
        if (v && !knownBeats.has(v)) {
          issues.push({
            kind: 'dangling-pointer',
            slideId: s.id,
            severity: 'error',
            title: `Dangling next.${k}: ${v}`,
            detail: `${s.label || s.id} routes to beat "${v}", which no longer exists. Pick a valid target in the Routing panel.`,
          });
        }
      }

      const triggers = (bm.adaptiveTriggers as
        | Record<string, { targetBeatId?: string }>
        | undefined) ?? {};
      for (const tk of Object.keys(triggers)) {
        const t = triggers[tk]?.targetBeatId;
        if (t && !knownBeats.has(t)) {
          issues.push({
            kind: 'dangling-pointer',
            slideId: s.id,
            severity: 'error',
            title: `Dangling adaptive trigger: ${tk}`,
            detail: `${s.label || s.id}'s ${tk} trigger points at beat "${t}", which no longer exists.`,
          });
        }
      }
    }

    // ---- quiz consistency ----
    const mcqs = collectMcqs(s);
    if (bm?.kind === 'quiz') {
      // Half-ghost: more than one mcq on a quiz slide.
      if (mcqs.length > 1) {
        issues.push({
          kind: 'half-ghost-quiz',
          slideId: s.id,
          severity: 'error',
          title: `Multiple MCQs on a quiz slide (${mcqs.length})`,
          detail:
            'A quiz slide holds exactly one question — only the first is scored, so extra questions here are ignored. Move each to its own chained quiz slide.',
        });
      }

      const mcq = mcqs[0];
      if (mcq) {
        const c = mcq.content as McqContent | undefined;
        const elementCorrect = c?.correctOptionId;
        const metaCorrect = bm.correctOptionId;
        const optionIds = c?.optionIds ?? [];

        // (a) correct-option mismatch
        if (elementCorrect && metaCorrect && elementCorrect !== metaCorrect) {
          issues.push({
            kind: 'correct-option-mismatch',
            slideId: s.id,
            severity: 'error',
            title: 'Correct option mismatch',
            detail: `MCQ element says "${elementCorrect}" is correct but beatMeta says "${metaCorrect}". Pick the right answer in the MCQ editor — the panels will resync.`,
          });
        }

        // (c) correct-option-unknown
        if (metaCorrect && optionIds.length > 0 && !optionIds.includes(metaCorrect)) {
          issues.push({
            kind: 'correct-option-unknown',
            slideId: s.id,
            severity: 'error',
            title: `Correct option "${metaCorrect}" doesn't exist`,
            detail:
              'beatMeta.correctOptionId names an option id that the MCQ element doesn\'t carry. Reset the correct answer in the MCQ editor.',
          });
        }

        // (b) branch-key mismatch (missing / extra) — only flag when we
        // have option ids to compare against.
        if (optionIds.length > 0) {
          const expectedBranchKeys = new Set(
            optionIds.filter((id) => id !== (metaCorrect ?? elementCorrect)),
          );
          const actualBranchKeys = new Set(Object.keys(bm.branches ?? {}));

          for (const k of expectedBranchKeys) {
            if (!actualBranchKeys.has(k)) {
              issues.push({
                kind: 'missing-branch',
                slideId: s.id,
                severity: 'warning',
                title: `Missing branch for option "${k}"`,
                detail: `No reteach branch defined for the wrong option "${k}". Wrong picks on this option will fall through to generalReteach.`,
              });
            }
          }
          for (const k of actualBranchKeys) {
            if (!expectedBranchKeys.has(k)) {
              issues.push({
                kind: 'extra-branch',
                slideId: s.id,
                severity: 'warning',
                title: `Stale branch for option "${k}"`,
                detail: `beatMeta.branches has an entry for "${k}" but the MCQ no longer has that option. Remove it or restore the option.`,
              });
            }
          }
        }
      }
    } else if (mcqs.length > 0) {
      // Ghost quiz: any mcq on a non-quiz slide.
      issues.push({
        kind: 'ghost-quiz',
        slideId: s.id,
        severity: 'error',
        title: `MCQ on a non-quiz slide (${bm?.kind ?? 'no kind'})`,
        detail:
          'This question isn’t on a quiz slide, so the lesson skips past it without waiting for an answer. Convert the slide to a quiz (Adaptive panel) or remove the question.',
      });
    }

    // entry-slide deletion safety check — if the entry beat id is set but
    // no slide carries it, the pack has no entry point at all. We don't
    // synthesize beatId for orphan slides, so this is best-effort.
    if (entryBeatId && beatId === null) continue; // skip non-beat slides
  }

  // ---- entry-beat missing ----
  if (entryBeatId && !knownBeats.has(entryBeatId)) {
    issues.push({
      kind: 'dangling-pointer',
      slideId: slides[0]?.id ?? '',
      severity: 'error',
      title: `Starting slide is missing`,
      detail:
        'The slide this lesson starts from no longer exists, so it can’t begin. Reopening the pack fixes this automatically; you can also set a new first slide.',
    });
  }

  // Cross-pointer dangling check: any slide pointing at a beatId that
  // matches the doomed-beat list? `findPointersTo` is the inverse of what
  // we need, but reusing it per beat id catches inbound danglers that the
  // per-slide outbound walk above missed. Keep this minimal: if a known
  // beat id has zero outbound issues, we're done.
  void findPointersTo; // (kept imported for the future "find references" UX)

  return issues;
}

function collectMcqs(slide: Slide): ScriptElement[] {
  const out: ScriptElement[] = [];
  for (const row of slide.rows) {
    for (const col of row.columns) {
      for (const el of col.elements) {
        if (el.type === 'mcq') out.push(el);
      }
    }
  }
  return out;
}
